import Link from "next/link";

import { useMemo } from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

import classNames from "classnames";
import type { Entity } from "models/entity";
import { twMerge } from "tailwind-merge";

import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";

import AddToWatchlistButton from "components/AddToWatchlistButton";
import EntityLogo from "components/EntityLogo";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import useAuthentication from "hooks/useAuthentication";
import highlightText from "utils/highLightText";
import shortSecurity from "utils/shortSecurity";

import PriceQuote from "./PriceQuote";

type EntitySnippetProps = {
  entity: Entity;
  query?: string;
  containerClassName?: string;
  logoClassName?: string;
  hideTicker?: boolean;
  customWatchlistCTA?: () => React.ReactNode;
  watchlistCtaAfterName?: boolean;
  showPriceQuote?: boolean;
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
};

const EntitySnippet = ({
  entity,
  query,
  containerClassName,
  logoClassName,
  hideTicker,
  customWatchlistCTA,
  watchlistCtaAfterName,
  showPriceQuote,
  children,
  onClick,
}: EntitySnippetProps) => {
  const { authenticated } = useAuthentication();

  const { toggleModal } = useSignInSignUp();

  const { slug, prettyName, security } = entity;

  const watchlistCTA = useMemo(() => {
    if (customWatchlistCTA) {
      return customWatchlistCTA();
    } else if (authenticated)
      return (
        <AddToWatchlistButton entity={entity}>
          {({ addToWatchlist, isWatching }) => (
            <div
              className={twMerge(
                isWatching
                  ? "bg-white border-bullish-green hover:bg-bullish-green text-bullish-green hover:text-white"
                  : "bg-white border-smart-teal  hover:bg-smart-teal text-smart-teal hover:text-white ",
                "border border-solid",
                "rounded-sm text-[12px] px-[1em] py-[0.75em]",
                "cursor-pointer"
              )}
              onClick={addToWatchlist}
            >
              <FontAwesomeIcon icon={isWatching ? faCheck : faPlus} />
            </div>
          )}
        </AddToWatchlistButton>
      );
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>Add to Watchlist</Tooltip>}
      >
        <div
          className={classNames(
            "flex",
            "items-center",
            "justify-center",
            "w-[35px]",
            "h-[35px]",
            "border",
            "border-solid",
            "border-smart-teal",
            "hover:bg-smart-teal",
            "text-smart-teal",
            "hover:text-white",
            "cursor-pointer"
          )}
          onClick={() => toggleModal("sign-in")}
        >
          <FontAwesomeIcon icon={faPlus} className="text-[12px] " />
        </div>
      </OverlayTrigger>
    );
  }, [authenticated, customWatchlistCTA, entity, toggleModal]);

  return (
    <div
      className={twMerge(
        "flex flex-row px-[15px] py-[10px] items-center space-x-[15px]",
        containerClassName
      )}
      onClick={onClick}
    >
      <Link
        href={`/entities/${slug}`}
        prefetch={false}
        className={twMerge(
          "min-w-[35px] h-[35px] mr-[10px] border border-solid border-[#00000022] flex items-center justify-center",
          logoClassName
        )}
      >
        <EntityLogo entity={entity} size={33} />
      </Link>
      <div className="flex-1">
        <div className="font-medium text-[14px]">
          <Link
            href={`/entities/${slug}`}
            passHref
            prefetch={false}
            className="hover:text-smart-teal"
          >
            {query ? highlightText(prettyName, query) : prettyName}
          </Link>
          {watchlistCtaAfterName && watchlistCTA}
        </div>
        {!hideTicker && (
          <div className="text-[12px] text-[#707070]">
            {query
              ? highlightText(shortSecurity(security), query)
              : shortSecurity(security)}
          </div>
        )}
      </div>
      {showPriceQuote && <PriceQuote entity={entity} />}
      {!watchlistCtaAfterName && watchlistCTA}
      {children}
    </div>
  );
};

export default EntitySnippet;
